// src/services/launchControlService.ts — the console's window onto the
// dark-launch runtime flags (GET/PUT /admin/launch-controls). The API's
// registry is the write allowlist; this client never invents keys. Flips are
// audited server-side (launch_control.updated, high severity).
import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useApiMutation, useApiQuery } from "../hooks/useApiRequest";
import { isTwoFactorCancelled } from "./twoFactorClient";
import { StepUpPromptReportedError } from "./twoFactorErrors";
import axios from "axios";
import {
  codeOf,
  isDualControlQueued,
  isPermissionError,
} from "../console/lib/apiError";
import {
  APPROVALS_KEY,
  type AdminApproval,
  type ApprovalsPage,
} from "./approvalsService";

export interface LaunchControl {
  key: string;
  title: string;
  /**
   * The api's grouping. Known values include support, compliance, payout,
   * mobile, auth, developer and security; the api adds domains without a
   * console release, so any string is accepted and an unknown one renders
   * neutral.
   */
  domain: string;
  description: string;
  enabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
  environments?: string[];
  /**
   * The cohort/scope the flag is currently granted to — e.g. "everyone" vs
   * "staff only". A flag can read `enabled` and still not be a grant to anyone
   * until it is attached to a cohort, so the scope has to be shown alongside the
   * on/off state; an enabled-but-ungranted flag must not read as active.
   *
   * Absent for a global flag — the API may serialize that as either a missing
   * field or an explicit `null`, so both are treated as "no cohort".
   */
  cohort?: string | null;
  /**
   * The cohort as DATA — what `cohort` describes in prose. Ids arrive as hex
   * strings whatever the API stores.
   *
   * OPTIONAL on purpose: the console and the API deploy independently, so a
   * console build can be live against an API that predates this field. Every
   * reader below falls back to the `cohort` string when it is absent, rather
   * than rendering a flag as ungranted because the API did not mention it.
   */
  rollout?: LaunchControlRollout | null;
  /**
   * The api marks a money step-up switch a security gate (ENG-1331): one reader,
   * fail-closed, and anything that narrows who it covers is a disarm that waits
   * for a second person. Absent on an api that predates it.
   */
  securityGate?: boolean;
  /**
   * The registry's value when no row is stored (the list spreads the api's
   * definition). `security.stepup_alerts` is ON by default in production only.
   */
  defaultEnabled?: boolean;
  /**
   * Who the control reaches as the api's own rollout reader reads it. Sent for
   * a security gate today, and for any control by an api that sends it. When it
   * is sent it is the answer for the status, the audience and the gate card,
   * whatever `rollout` looks like: read it through `apiCoverage`. Absent on an
   * older api, where the console derives the reach from `rollout`.
   */
  coverage?: SecurityGateCoverageSummary | null;
  /**
   * The api's guard: `true` on an enabled row that carries no explicit rollout
   * percentage. Sent by an api whose reader reads such a row by the decided
   * rule (nobody named reaches nobody, people named reach only them), so the
   * flag is also the console's per-row proof that this reader is deployed.
   * Absent on an older api. Only an explicit `true` counts: absent means "not
   * flagged", and `false` is never read as information.
   */
  misconfigured?: boolean;
  /** A security gate only: its latest audited change, absent when it has none. */
  lastChange?: SecurityGateLastChange | null;
  /**
   * `support.customer_access_gating_enabled` only: whether the ENG-540 capability
   * seed has run on this environment (finance and compliance hold
   * `customer.pii.view`). CONTRACT GAP: ENG-1337 says its entry carries this
   * readiness flag but has not named the field yet; read it only through
   * `piiCapabilitySeedReady` so the name changes in one place.
   */
  capabilitySeedReady?: boolean;
}

export interface LaunchControlRollout {
  userIds: string[];
  businessIds: string[];
  percentage?: number;
  /**
   * The people `userIds` names. Same order and same length as `userIds` — the
   * API reports an id nobody carries rather than dropping it, so this never
   * silently loses a row.
   *
   * Optional because an API predating it sends no `users`; readers fall back to
   * the raw ids.
   */
  users?: LaunchControlCohortUser[] | null;
}

/** Who a control covers, as the api's list sends it. */
export interface SecurityGateCoverageSummary {
  kind: "nobody" | "cohort" | "everyone";
  /** The cohort's size; only when `kind` is cohort. */
  people?: number;
  /** In words ("everyone", "nobody", "3 people"); never a user id. */
  label?: string;
}

/** A person on a gate's last change: an id and a display name. */
export interface SecurityGatePerson {
  id: string;
  name: string;
}

/** Widening is an arm, any narrowing a disarm, no change in coverage neutral. */
export type SecurityGateChangeKind = "arm" | "disarm" | "neutral";

/** A security gate's latest audited change, as the api's list sends it. */
export interface SecurityGateLastChange {
  at: string;
  /** Who made it; null for the seed or a detected out-of-band write. */
  by: SecurityGatePerson | null;
  change: SecurityGateChangeKind;
  /** The second person, on a two-person disarm. */
  approvedBy?: SecurityGatePerson | null;
  source?: "console" | "seed" | "detected";
}

/**
 * The disarm rule every security gate follows, read from the api's
 * securityGateDisarmPolicy (ENG-1331). The console words its warnings from
 * this and hard-codes none of it; absent on an api that does not send it.
 */
export interface SecurityGateDisarmRule {
  twoPeople: boolean;
  operatorTotp: boolean;
  rollbackWindowMinutes: number;
  approverRoleKeys: string[];
}

export interface LaunchControlList {
  controls: LaunchControl[];
  securityGateDisarmRule?: SecurityGateDisarmRule | null;
}

/** One named member of a cohort. */
export interface LaunchControlCohortUser {
  id: string;
  /** Display name, or the raw id when the API holds no name for them. */
  name: string;
  email?: string | null;
  status?: string | null;
  /** False when NO user carries this id — the grant does nothing. */
  found: boolean;
  /** The account exists but is soft-deleted. Also a dead grant. */
  deleted: boolean;
}

/**
 * A cohort member whose grant does not actually reach anybody: an id nobody
 * carries, a deleted account, or a disabled one. Worth calling out — these are
 * the rows an operator would otherwise read as a working grant.
 */
export const isDeadGrant = (user: LaunchControlCohortUser): boolean =>
  !user.found || user.deleted || user.status === "disabled";

/** The cohort members, falling back to bare ids against an older API. */
export const cohortMembers = (
  rollout?: LaunchControlRollout | null
): LaunchControlCohortUser[] => {
  if (!rollout) return [];
  if (rollout.users && rollout.users.length) return rollout.users;
  return (rollout.userIds ?? []).map((id) => ({
    id,
    name: id,
    found: true,
    deleted: false,
  }));
};

/**
 * The live status of a launch control, reconciled with its cohort.
 *
 * `dark` — off. `live` — on and reaching everyone. `scoped` — on and reaching a
 * named subset, which is a real grant but NOT a full launch. `ungranted` — on
 * and reaching nobody.
 *
 * `scoped` exists because production's `admin_mobile_modules` is exactly that
 * case — enabled, with two named users and percentage 0 — and both of the states
 * it used to collapse into were lies: "Live" reads as everyone, "ungranted"
 * reads as nobody, and the truth is two people.
 */
export type LaunchStatus = "dark" | "live" | "scoped" | "ungranted";

/** A cohort value that actually grants the flag to someone. An absent, empty,
 *  whitespace-only, "none" or "off" cohort is a cohort that grants to nobody. */
export const isRealCohort = (cohort?: string | null): boolean => {
  const trimmed = cohort?.trim();
  return Boolean(trimmed) && trimmed !== "none" && trimmed !== "off";
};

/** How many people a rollout names outright. */
export const cohortSize = (rollout?: LaunchControlRollout | null): number =>
  (rollout?.userIds?.length ?? 0) + (rollout?.businessIds?.length ?? 0);

/**
 * Whether a rollout reaches anyone at all. A named target is an allowlist and
 * grants regardless of percentage — the API evaluates it first — so a cohort of
 * two people with percentage 0 reaches those two.
 */
const rolloutGrantsAnyone = (rollout: LaunchControlRollout): boolean =>
  cohortSize(rollout) > 0 || (rollout.percentage ?? 0) > 0;

/**
 * Whether a rollout reaches EVERYONE, i.e. is not really a cohort. A rollout
 * that names nobody and carries no percentage reads as everyone only when
 * `absentPercentageIsEveryone`: the older api's generic reader serves such a
 * row to everyone, the decided rule serves it to nobody.
 */
const rolloutIsGlobal = (
  rollout: LaunchControlRollout,
  absentPercentageIsEveryone: boolean
): boolean =>
  cohortSize(rollout) === 0 &&
  (rollout.percentage ?? (absentPercentageIsEveryone ? 100 : 0)) >= 100;

/** The balance-payment factor switch (ENG-1329). */
export const BALANCE_PAY_STEPUP_KEY = "auth.balance_pay_stepup_enforced";

/** The customer-PII gate (ENG-1337), whose ON needs the ENG-540 seed. */
export const CUSTOMER_PII_GATING_KEY = "support.customer_access_gating_enabled";

/** Whether the api flags the control as switched on with no explicit percentage. */
export const isMisconfigured = (
  control: Pick<LaunchControl, "misconfigured">
): boolean => control.misconfigured === true;

const COVERAGE_KINDS: ReadonlySet<string> = new Set([
  "nobody",
  "cohort",
  "everyone",
]);

/**
 * The api's own answer to who a control reaches, when it sends one this build
 * can read. The status pills and the gate cards both take the api's answer from
 * here, so the two cannot drift. The list reaches the console unvalidated, so a
 * kind this build does not know is ignored rather than trusted.
 */
export const apiCoverage = (
  control: Pick<LaunchControl, "coverage">
): SecurityGateCoverageSummary | undefined => {
  const coverage = control.coverage;
  return coverage && COVERAGE_KINDS.has(coverage.kind) ? coverage : undefined;
};

const NO_TARGETING: LaunchControlRollout = { userIds: [], businessIds: [] };

/**
 * The stored rollout to read, and how to read a missing percentage. A row the
 * api flags misconfigured is read by the decided rule, and a flagged row with
 * no rollout at all names nobody. Every other row keeps the older reading.
 */
const storedReach = (control: LaunchControl) => {
  const flagged = isMisconfigured(control);
  return {
    rollout: control.rollout ?? (flagged ? NO_TARGETING : undefined),
    absentPercentageIsEveryone: !flagged,
  };
};

const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

/** The audience pill for the api's own coverage. */
const coverageSummary = (coverage: SecurityGateCoverageSummary): string => {
  if (coverage.kind === "everyone") return "";
  if (coverage.kind === "nobody") return "nobody yet";
  const label = coverage.label?.trim();
  if (label) return label;
  return typeof coverage.people === "number"
    ? people(coverage.people)
    : "a cohort";
};

export const launchStatus = (control: LaunchControl): LaunchStatus => {
  if (!control.enabled) return "dark";
  const coverage = apiCoverage(control);
  if (coverage)
    return coverage.kind === "everyone"
      ? "live"
      : coverage.kind === "cohort"
        ? "scoped"
        : "ungranted";

  const { rollout, absentPercentageIsEveryone } = storedReach(control);
  if (rollout) {
    if (rolloutIsGlobal(rollout, absentPercentageIsEveryone)) return "live";
    return rolloutGrantsAnyone(rollout) ? "scoped" : "ungranted";
  }

  // No structured rollout: either a genuinely global flag, or an API too old to
  // send one. Fall back to the prose cohort, which is what shipped before.
  if (control.cohort != null && !isRealCohort(control.cohort))
    return "ungranted";
  return control.cohort != null ? "scoped" : "live";
};

/**
 * The one-line audience summary shown next to the status — "2 named users",
 * "25% of users". Empty when the flag reaches everyone and there is nothing to
 * qualify. The api's coverage wins while the control is ON; an OFF control
 * keeps describing the cohort it would reach.
 */
export const cohortSummary = (control: LaunchControl): string => {
  const coverage = control.enabled ? apiCoverage(control) : undefined;
  if (coverage) return coverageSummary(coverage);
  const { rollout, absentPercentageIsEveryone } = storedReach(control);
  if (!rollout) return control.cohort?.trim() ?? "";
  if (rolloutIsGlobal(rollout, absentPercentageIsEveryone)) return "";

  const parts: string[] = [];
  const members = cohortMembers(rollout);
  const users = rollout.userIds?.length ?? 0;
  const businesses = rollout.businessIds?.length ?? 0;
  // Two or three names read better than a count; past that the count does. The
  // names come from the API, so this degrades to the count against an older one.
  if (users) {
    const named = members.every((m) => m.name !== m.id) && members.length <= 3;
    parts.push(
      named
        ? members.map((m) => m.name).join(", ")
        : `${users} named user${users === 1 ? "" : "s"}`
    );
  }
  if (businesses)
    parts.push(`${businesses} named business${businesses === 1 ? "" : "es"}`);
  const pct = rollout.percentage;
  if (pct !== undefined && pct > 0 && pct < 100) parts.push(`${pct}% of users`);
  if (parts.length) return parts.join(" + ");
  return "nobody yet";
};

/** The warning pill on a control the api flags misconfigured. */
export const MISCONFIGURED_PILL = "No explicit rollout";

/**
 * What a control the api flags misconfigured does, in one sentence and never
 * with an id: switched on with no explicit percentage, it reaches nobody, or
 * only those named, until someone sets one. `verb` is "reaches" for a control
 * and "protects" for a security gate. `reachesNamed` is the card's own reading,
 * never a second one taken from the stored rollout: a gate ignores businesses
 * and the api's coverage beats the rollout, so the sentence could otherwise
 * contradict the pill above it.
 */
export const misconfiguredSentence = (
  reachesNamed: boolean,
  verb: "reaches" | "protects"
): string =>
  reachesNamed
    ? `Switched on with no explicit rollout percentage, so it ${verb} only those named until someone sets 100 for everyone, or 0 to keep just them.`
    : `Switched on with no explicit rollout percentage, so it ${verb} nobody until someone sets 100 for everyone, or 0 with the people named.`;

/**
 * Whether the ENG-540 capability seed has run here, for the PII-gating card.
 * Only an explicit `true` counts: an api that has not sent the flag yet must not
 * read as "safe to switch on" (production lacks the seed today).
 */
export const piiCapabilitySeedReady = (control: LaunchControl): boolean =>
  control.capabilitySeedReady === true;

/**
 * What a flip or a cohort edit came to. `queued` is the dual-control answer
 * (409 DUAL_CONTROL_SENT_FOR_APPROVAL and its siblings): nothing failed, the
 * change waits for a second person, and `detail` is the api's own message for
 * the card to show. It is never an error toast.
 */
export interface LaunchChangeOutcome {
  ok: boolean;
  queued: boolean;
  detail?: string;
}

const LAUNCH_CONTROLS_KEY = ["system", "launch-controls"] as const;
const LAUNCH_CONTROL_AUDIT_KEY = ["system", "launch-control-audit"] as const;

/**
 * An EMERGENCY STOP: a control whose ON state stops something, where every
 * other control's ON state launches something. The switchboard was written for
 * launches, so a stop read backwards on it: turning the 88 Race freeze on asked
 * "Launch “88 Race emergency freeze”?" and then showed it as a green "Live".
 *
 * Told apart by the key, which is the only thing the API sends that says so:
 * the freezes end `.global_freeze`, the messaging stop ends `_kill_switch`.
 */
const EMERGENCY_STOP_KEY = /(\.global_freeze|_kill_switch)$/;
export const isEmergencyStop = (key: string): boolean =>
  EMERGENCY_STOP_KEY.test(key);

export const useLaunchControls = (options?: {
  /** Re-read on a timer, for a page that shows one control's state as live. */
  refetchInterval?: number;
}) => {
  return useApiQuery<LaunchControlList>({
    key: [...LAUNCH_CONTROLS_KEY],
    endPoint: "/api/v2/admin/launch-controls",
    method: "GET",
    ...(options?.refetchInterval
      ? { queryOptions: { refetchInterval: options.refetchInterval } }
      : {}),
  });
};

export const useUpdateLaunchControl = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useApiMutation();

  const setEnabled = async (
    key: string,
    enabled: boolean,
    onSuccess?: () => void
  ): Promise<LaunchChangeOutcome> => {
    try {
      await mutateAsync({
        key: [...LAUNCH_CONTROLS_KEY, "update"],
        endPoint: `/api/v2/admin/launch-controls/${encodeURIComponent(key)}`,
        method: "PUT",
        bodyContent: { enabled },
        // No code field on this screen: a step-up refusal opens the operator's one
        // prompt and the identical request is re-sent with the first code (ENG-1378).
        stepUp: "host",
        stepUpAction: enabled
          ? "Launching this control"
          : "Switching this control off",
      });
      await queryClient.invalidateQueries({
        queryKey: [...LAUNCH_CONTROLS_KEY],
      });
      message.success(
        enabled
          ? "Launched — live within ~30s on every instance."
          : "Switched off — dark within ~30s on every instance."
      );
      onSuccess?.();
      return { ok: true, queued: false };
    } catch (err) {
      // A disarm or another two-person change came back queued: it waits for a
      // second approver, which the card says in place of an error (ENG-1366).
      if (isDualControlQueued(err)) {
        await queryClient.invalidateQueries({
          queryKey: [...LAUNCH_CONTROLS_KEY],
        });
        return { ok: false, queued: true, detail: (err as Error).message };
      }
      // "Not now" on the step-up prompt: nothing was sent, by the operator's choice.
      if (isTwoFactorCancelled(err)) {
        // The prompt closed on a lost answer: the change may have landed, so
        // refresh the cards. "Not now" sent nothing and needs no refresh.
        if (err instanceof StepUpPromptReportedError) {
          await queryClient.invalidateQueries({
            queryKey: [...LAUNCH_CONTROLS_KEY],
          });
        }
        return { ok: false, queued: false };
      }
      message.error(
        err instanceof Error
          ? err.message
          : "Could not update the launch control"
      );
      return { ok: false, queued: false };
    }
  };

  return { setEnabled, isPending };
};

export interface CohortChange {
  addUserIds?: string[];
  removeUserIds?: string[];
  percentage?: number;
}

/**
 * Amend a control's rollout cohort. Deltas only — the API takes adds and
 * removes, never a replacement list, so nothing this hook can send will empty a
 * cohort by accident. Audited server-side as `launch_control.cohort_updated`.
 */
export const useUpdateLaunchCohort = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useApiMutation();

  const updateCohort = async (
    key: string,
    change: CohortChange,
    onSuccess?: () => void
  ): Promise<LaunchChangeOutcome> => {
    try {
      await mutateAsync({
        key: [...LAUNCH_CONTROLS_KEY, "cohort", key],
        endPoint: `/api/v2/admin/launch-controls/${encodeURIComponent(
          key
        )}/cohort`,
        method: "PATCH",
        bodyContent: change,
        stepUp: "host",
        stepUpAction: "Updating this cohort",
      });
      await queryClient.invalidateQueries({
        queryKey: [...LAUNCH_CONTROLS_KEY],
      });
      message.success("Cohort updated — live within ~30s on every instance.");
      onSuccess?.();
      return { ok: true, queued: false };
    } catch (err) {
      if (isDualControlQueued(err)) {
        await queryClient.invalidateQueries({
          queryKey: [...LAUNCH_CONTROLS_KEY],
        });
        return { ok: false, queued: true, detail: (err as Error).message };
      }
      if (isTwoFactorCancelled(err)) {
        // The prompt closed on a lost answer: the change may have landed, so
        // refresh the cards. "Not now" sent nothing and needs no refresh.
        if (err instanceof StepUpPromptReportedError) {
          await queryClient.invalidateQueries({
            queryKey: [...LAUNCH_CONTROLS_KEY],
          });
        }
        return { ok: false, queued: false };
      }
      message.error(
        err instanceof Error ? err.message : "Could not update the cohort"
      );
      return { ok: false, queued: false };
    }
  };

  return { updateCohort, isPending };
};

// ── Security gates (ENG-1385) ───────────────────────────────────────────────
// The api's own approval action keys for a security-gate disarm (ENG-1331):
// turning a gate OFF (the PUT) and narrowing its cohort (the cohort PATCH).
export const SECURITY_GATE_DISABLE_APPROVAL_KEY =
  "launch_control.security.disable";
export const SECURITY_GATE_COHORT_APPROVAL_KEY =
  "launch_control.security.cohort";
export const SECURITY_GATE_APPROVAL_KEYS: readonly string[] = [
  SECURITY_GATE_DISABLE_APPROVAL_KEY,
  SECURITY_GATE_COHORT_APPROVAL_KEY,
];

/** 400 on a security gate's cohort with a percentage other than 0 or 100. */
export const SECURITY_GATE_COHORT_USERS_ONLY_CODE =
  "SECURITY_GATE_COHORT_USERS_ONLY";

/**
 * 409 from the api's own check before it writes: the change narrows who the
 * gate covers but was not cleared, because the row changed under the request.
 */
export const SECURITY_GATE_DISARM_NOT_APPROVED_CODE =
  "SECURITY_GATE_DISARM_NOT_APPROVED";

/** One change to a security gate, exactly as it is sent. */
export type SecurityGateRequest =
  | { kind: "toggle"; key: string; enabled: boolean }
  | { kind: "cohort"; key: string; change: CohortChange };

/**
 * What a change to a security gate came to. The gate card renders each one
 * in place; none of them is a toast, because the dangerous answers (sent for
 * approval, already waiting, a different approved change) are not failures
 * and a red error would invite a retry.
 */
export type SecurityGateOutcome =
  | { kind: "applied" }
  /** 409 DUAL_CONTROL_SENT_FOR_APPROVAL: filed for a second approver. */
  | { kind: "sent"; detail: string }
  /** 409 DUAL_CONTROL_AWAITING_APPROVAL: one is already waiting. */
  | { kind: "awaiting"; detail: string }
  /** 409 DUAL_CONTROL_PARAMS_CHANGED: an approval exists for other values. */
  | { kind: "params-changed"; detail: string }
  /** 400 SECURITY_GATE_COHORT_USERS_ONLY. */
  | { kind: "cohort-users-only" }
  /** A plain 403: the operator may not change launch controls. */
  | { kind: "permission" }
  /** No answer: the change may or may not have landed. */
  | { kind: "network" }
  /** The step-up prompt was dismissed or answered for us (ENG-1378). */
  | { kind: "cancelled" }
  /** 409 SECURITY_GATE_DISARM_NOT_APPROVED: the row changed under the request. */
  | { kind: "changed-meanwhile"; detail: string }
  | { kind: "error"; detail: string };

const DUAL_CONTROL_SENT = "DUAL_CONTROL_SENT_FOR_APPROVAL";
const DUAL_CONTROL_AWAITING = "DUAL_CONTROL_AWAITING_APPROVAL";
const DUAL_CONTROL_PARAMS_CHANGED = "DUAL_CONTROL_PARAMS_CHANGED";

/** A request that got no answer at all (offline, timed out, reset). */
const isNoAnswer = (err: unknown): boolean =>
  axios.isAxiosError(err) && !err.response;

/** Read a refused gate change into the outcome its card renders. */
export const securityGateOutcomeOf = (err: unknown): SecurityGateOutcome => {
  if (isTwoFactorCancelled(err)) return { kind: "cancelled" };
  const detail = err instanceof Error ? err.message : "";
  if (isDualControlQueued(err)) {
    const code = codeOf(err);
    if (code === DUAL_CONTROL_AWAITING) return { kind: "awaiting", detail };
    if (code === DUAL_CONTROL_PARAMS_CHANGED)
      return { kind: "params-changed", detail };
    // DUAL_CONTROL_SENT_FOR_APPROVAL, or a pre-code api's queued wording.
    return { kind: "sent", detail };
  }
  if (codeOf(err) === SECURITY_GATE_COHORT_USERS_ONLY_CODE)
    return { kind: "cohort-users-only" };
  if (codeOf(err) === SECURITY_GATE_DISARM_NOT_APPROVED_CODE)
    return { kind: "changed-meanwhile", detail };
  // Step-up refusals never reach here: the shared prompt host (ENG-1378)
  // answers them and settles this call with the re-send's own answer.
  if (isPermissionError(err)) return { kind: "permission" };
  if (isNoAnswer(err)) return { kind: "network" };
  return { kind: "error", detail: detail || "The change was not applied." };
};

/**
 * Send one change to a security gate through the shared step-up path: a
 * disarm asks for the operator's own code, which the one prompt host collects
 * and re-sends with the identical request (ENG-1378). Nothing here toasts.
 */
export const useSecurityGateChange = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useApiMutation();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [...LAUNCH_CONTROLS_KEY] }),
      queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] }),
      queryClient.invalidateQueries({
        queryKey: [...LAUNCH_CONTROL_AUDIT_KEY],
      }),
    ]);

  const send = async (
    request: SecurityGateRequest
  ): Promise<SecurityGateOutcome> => {
    try {
      await mutateAsync(
        request.kind === "toggle"
          ? {
              key: [...LAUNCH_CONTROLS_KEY, "update"],
              endPoint: `/api/v2/admin/launch-controls/${encodeURIComponent(
                request.key
              )}`,
              method: "PUT",
              bodyContent: { enabled: request.enabled },
              stepUp: "host",
              stepUpAction: request.enabled
                ? "Turning on a security gate"
                : "Turning off a security gate",
            }
          : {
              key: [...LAUNCH_CONTROLS_KEY, "cohort", request.key],
              endPoint: `/api/v2/admin/launch-controls/${encodeURIComponent(
                request.key
              )}/cohort`,
              method: "PATCH",
              bodyContent: request.change,
              stepUp: "host",
              stepUpAction: "Changing who a security gate protects",
            }
      );
      await refresh();
      return { kind: "applied" };
    } catch (err) {
      const outcome = securityGateOutcomeOf(err);
      // "Not now" sent nothing; every other answer may have changed the row or
      // the approvals, so both are read again.
      if (
        outcome.kind !== "cancelled" ||
        err instanceof StepUpPromptReportedError
      )
        await refresh();
      return outcome;
    }
  };

  return { send, isPending };
};

/**
 * The security-gate change an approval was filed for, rebuilt from the
 * approval's own record so the requester can re-send the identical request
 * once it is approved (the api consumes the approval on that re-send). Null
 * when the record does not say which gate, or carries a body this console
 * would not send as it is.
 */
export const securityGateRequestOfApproval = (
  approval: AdminApproval
): SecurityGateRequest | null => {
  const key = securityGateOfApproval(approval)?.key;
  const metadata = approval.metadata ?? {};
  const params = metadata.params;
  if (!key || !params || typeof params !== "object" || Array.isArray(params))
    return null;
  const body = params as Record<string, unknown>;
  if (approval.action === SECURITY_GATE_DISABLE_APPROVAL_KEY) {
    if (metadata.method !== "PUT" || typeof body.enabled !== "boolean")
      return null;
    if (Object.keys(body).length !== 1) return null;
    return { kind: "toggle", key, enabled: body.enabled };
  }
  if (approval.action === SECURITY_GATE_COHORT_APPROVAL_KEY) {
    if (metadata.method !== "PATCH") return null;
    const ids = (value: unknown): string[] | null | undefined =>
      value === undefined
        ? undefined
        : Array.isArray(value) && value.every((v) => typeof v === "string")
          ? (value as string[])
          : null;
    const addUserIds = ids(body.addUserIds);
    const removeUserIds = ids(body.removeUserIds);
    if (addUserIds === null || removeUserIds === null) return null;
    if (body.percentage !== undefined && typeof body.percentage !== "number")
      return null;
    const known = ["addUserIds", "removeUserIds", "percentage"];
    if (Object.keys(body).some((k) => !known.includes(k))) return null;
    return {
      kind: "cohort",
      key,
      change: {
        ...(addUserIds ? { addUserIds } : {}),
        ...(removeUserIds ? { removeUserIds } : {}),
        ...(typeof body.percentage === "number"
          ? { percentage: body.percentage }
          : {}),
      },
    };
  }
  return null;
};

/**
 * What a security-gate approval says about its gate (ENG-1331 files it as
 * `metadata.securityGate`): key, title, the change, and who it covers from
 * and to, in words. Null on an api that does not name the gate.
 */
export interface SecurityGateApprovalTarget {
  key: string;
  title?: string;
  change?: string;
  from?: string;
  to?: string;
}

export const securityGateOfApproval = (
  approval: AdminApproval
): SecurityGateApprovalTarget | null => {
  if (!SECURITY_GATE_APPROVAL_KEYS.includes(approval.action)) return null;
  const raw = approval.metadata?.securityGate;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const gate = raw as Record<string, unknown>;
  if (typeof gate.key !== "string" || !gate.key) return null;
  const text = (value: unknown) =>
    typeof value === "string" && value ? value : undefined;
  return {
    key: gate.key,
    title: text(gate.title),
    change: text(gate.change),
    from: text(gate.from),
    to: text(gate.to),
  };
};

/**
 * Security-gate requests in the approvals queue: pending ones, and approved
 * ones not yet applied (an approval is consumed by the re-send). The list
 * has no action filter, so both pages are read and filtered here.
 */
export const useSecurityGateApprovals = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const read = (status: "pending" | "approved") => ({
    key: [APPROVALS_KEY, "security-gates", status],
    endPoint: `/api/v2/admin/approvals?status=${status}&page=1&limit=100`,
    enabled,
    queryOptions: { refetchInterval: 30_000, retry: false },
  });
  const pending = useApiQuery<ApprovalsPage>(read("pending"));
  const approved = useApiQuery<ApprovalsPage>(read("approved"));
  const ofGates = (page?: ApprovalsPage) =>
    (page?.items ?? []).filter((a) =>
      SECURITY_GATE_APPROVAL_KEYS.includes(a.action)
    );
  // An approval past its expiry can no longer be consumed: re-sending it would
  // file a new request, so it is not offered as "approved, not yet applied".
  const live = (a: AdminApproval) =>
    !a.consumedAt && !(a.expiresAt && Date.parse(a.expiresAt) <= Date.now());
  // The list is capped and has no action filter: when more requests wait than
  // were read, a gate request may be among the unread ones, and the page says so.
  const truncated = (page?: ApprovalsPage) =>
    Boolean(page && page.total > (page.items?.length ?? 0));
  return {
    pending: ofGates(pending.data),
    approved: ofGates(approved.data).filter(live),
    truncated: truncated(pending.data) || truncated(approved.data),
    isLoading: pending.isLoading || approved.isLoading,
    isError: pending.isError || approved.isError,
    refetch: () => {
      void pending.refetch();
      void approved.refetch();
    },
  };
};

/** An audit row for a launch control, as GET /admin/audit returns it. */
export interface LaunchControlAuditRow {
  _id?: string;
  action: string;
  createdAt?: string;
  targetType?: string;
  targetKey?: string;
  success?: boolean;
  actorRole?: string;
  userId?:
    | {
        _id?: string;
        email?: string;
        personal_info?: {
          fullName?: string;
          firstName?: string;
          lastName?: string;
          name?: string;
        };
      }
    | string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

/**
 * Launch-control audit rows, for a gate's history (there is no per-gate
 * history route). Filtered by action prefix and target type; the caller keeps
 * the rows whose `targetKey` is its gate. Needs `audit.view`.
 */

export const useLaunchControlAudit = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  // Two action prefixes: console and seed changes are `launch_control.*`, and
  // a change made outside the console is `security_gate.unaudited_change_detected`.
  const read = (prefix: string) => ({
    key: [...LAUNCH_CONTROL_AUDIT_KEY, prefix],
    endPoint: `/api/v2/admin/audit?action=${prefix}&targetType=operational_policy&page=1&limit=100`,
    enabled,
    queryOptions: { retry: false },
  });
  const changes = useApiQuery<{ logs?: LaunchControlAuditRow[] }>(
    read("launch_control.")
  );
  const detected = useApiQuery<{ logs?: LaunchControlAuditRow[] }>(
    read("security_gate.")
  );
  const loaded = changes.data || detected.data;
  return {
    data: loaded
      ? {
          logs: [...(changes.data?.logs ?? []), ...(detected.data?.logs ?? [])],
        }
      : undefined,
    isLoading: changes.isLoading || detected.isLoading,
    isError: changes.isError || detected.isError,
    error: changes.error ?? detected.error,
    refetch: () => {
      void changes.refetch();
      void detected.refetch();
    },
  };
};
